import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Play,
  Pause,
  Maximize2,
  Sparkles,
  ShieldCheck,
  Heart,
  BookOpen,
  Compass,
  CheckCircle2,
  ExternalLink,
  Volume2,
  Film,
  Layers,
  ChevronRight,
  User,
  Clock,
  ArrowRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface VideoChapter {
  time: string;
  seconds: number;
  title: string;
  description: string;
  tag: string;
}

const CHAPTERS: VideoChapter[] = [
  { time: '00:00', seconds: 0, title: 'Opening & Vision', description: 'meUusSoulAI: A Guided Reflection Pilot for Human Development', tag: 'Introduction' },
  { time: '00:12', seconds: 12, title: 'What It Is', description: 'A small, honest, private experiment — max 5 messages for calm clarity', tag: 'Core Concept' },
  { time: '00:24', seconds: 24, title: 'Consent & Safety Boundary', description: 'Explicit age and boundary confirmation before reflection begins', tag: 'Ethics & Protocol' },
  { time: '00:44', seconds: 44, title: 'One Real Question', description: '"How are you — really?" User writes one honest sentence', tag: 'Reflection Mode' },
  { time: '01:04', seconds: 64, title: 'Under The Hood N8N Workflow', description: 'Message in, reflection out. Structured data mapping & privacy guardrails', tag: 'Architecture' },
  { time: '01:19', seconds: 79, title: 'Known Limitations Stated Clearly', description: 'Not medical diagnosis, not therapy — pure ethical companion support', tag: 'Transparency' },
  { time: '01:35', seconds: 95, title: 'The Covenant: me → U → us', description: 'From Bangladesh to the World — connecting every dimension of life', tag: 'The Mission' }
];

export default function VideoExperienceHub() {
  const [selectedVideo, setSelectedVideo] = useState<'soul-pilot' | 'ecosystem-vision'>('soul-pilot');
  const [activeChapter, setActiveChapter] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);

  const videoUrl = selectedVideo === 'soul-pilot' 
    ? `https://www.youtube.com/embed/0Hydbz9nykQ?autoplay=1&mute=1&enablejsapi=1&rel=0&start=${CHAPTERS[activeChapter].seconds}`
    : `https://www.youtube.com/embed/0Hydbz9nykQ?autoplay=1&mute=1&enablejsapi=1&rel=0`;

  return (
    <section id="video-experience" className="py-20 px-6 bg-slate-950 text-white relative overflow-hidden border-t border-slate-800">
      {/* Background Glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-600/10 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto space-y-10">
        {/* Section Title */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-800 pb-8">
          <div className="space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono uppercase tracking-wider">
              <Film className="w-3.5 h-3.5" />
              <span>Official Video Experience & Pilot Demonstration</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-tight text-white leading-tight">
              Watch <span className="text-emerald-400">meUus in Action</span>
            </h2>
            <p className="text-slate-300 text-sm md:text-base leading-relaxed">
              Explore the official walkthrough of the meUusSoulAI Guided Reflection Pilot and the foundational vision: from solving solitary hardship to building a connected human ecosystem for Bangladesh.
            </p>
          </div>

          {/* Video Selector Tabs */}
          <div className="flex items-center gap-2 bg-slate-900/90 p-1.5 rounded-2xl border border-slate-800">
            <button
              onClick={() => setSelectedVideo('soul-pilot')}
              className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all flex items-center gap-2 ${
                selectedVideo === 'soul-pilot'
                  ? 'bg-emerald-600 text-white shadow-lg'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>meUusSoulAI Pilot</span>
            </button>

            <button
              onClick={() => setSelectedVideo('ecosystem-vision')}
              className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all flex items-center gap-2 ${
                selectedVideo === 'ecosystem-vision'
                  ? 'bg-emerald-600 text-white shadow-lg'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>The 9 Hubs Vision</span>
            </button>
          </div>
        </div>

        {/* Video Player & Interactive Chapter Timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Main Video Viewport */}
          <div className="lg:col-span-8 space-y-4">
            <div className="relative aspect-video rounded-3xl bg-slate-900 border border-slate-800 overflow-hidden shadow-2xl">
              <iframe
                src={videoUrl}
                title="meUus Official Presentation & Guided Reflection Pilot"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full border-0"
              />
            </div>

            {/* Video Meta Info Bar */}
            <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs font-mono">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center font-bold">
                  m
                </div>
                <div>
                  <div className="font-bold text-white">"meUusSoulAI" The Starting Point _ A Guided Reflection Pilot</div>
                  <div className="text-slate-400 text-[11px]">Presented by Md. Mahady Hasan · Devpost AI Hackathon Demo</div>
                </div>
              </div>

              <a
                href="https://www.youtube.com/watch?v=0Hydbz9nykQ"
                target="_blank"
                rel="noreferrer"
                className="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white transition-colors flex items-center gap-1.5 text-[11px] self-start sm:self-auto"
              >
                <span>Watch on YouTube</span>
                <ExternalLink className="w-3.5 h-3.5 text-emerald-400" />
              </a>
            </div>
          </div>

          {/* Interactive Chapter Timeline Sidebar (Microsoft / Netflix Chapter List Pattern) */}
          <div className="lg:col-span-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-mono uppercase tracking-wider text-emerald-400 font-bold flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>Jump to Video Chapter:</span>
              </h4>
              <span className="text-[10px] font-mono text-slate-500">7 Milestones</span>
            </div>

            <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
              {CHAPTERS.map((ch, idx) => {
                const isActive = activeChapter === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveChapter(idx)}
                    className={`w-full p-3.5 rounded-2xl border text-left transition-all flex items-start gap-3 group ${
                      isActive
                        ? 'bg-slate-900 border-emerald-500 text-white shadow-lg ring-1 ring-emerald-500/50'
                        : 'bg-slate-900/40 border-slate-800 text-slate-400 hover:bg-slate-900 hover:text-white'
                    }`}
                  >
                    <div className={`px-2 py-1 rounded-lg font-mono text-[10px] font-bold shrink-0 mt-0.5 ${
                      isActive ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800 text-emerald-400'
                    }`}>
                      {ch.time}
                    </div>

                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex items-center justify-between">
                        <h5 className="font-serif font-bold text-xs text-white truncate group-hover:text-emerald-300 transition-colors">
                          {ch.title}
                        </h5>
                        <span className="text-[9px] font-mono text-slate-500 uppercase">{ch.tag}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 line-clamp-2 leading-relaxed">
                        {ch.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* The 3 Core Video Narratives (Quoted Highlights) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-800">
          <div className="p-5 rounded-3xl bg-slate-900/60 border border-slate-800 space-y-2">
            <span className="text-[10px] font-mono uppercase text-emerald-400 font-bold">The Problem In Bangladesh</span>
            <p className="text-xs text-slate-300 italic font-serif leading-relaxed">
              “In Bangladesh, people don’t face one problem. They face everything at once: no job, no legal help, no mental support, and no direction. The system treats them like separate problems. meUus walks beside the whole person.”
            </p>
          </div>

          <div className="p-5 rounded-3xl bg-slate-900/60 border border-slate-800 space-y-2">
            <span className="text-[10px] font-mono uppercase text-teal-400 font-bold">The Guiding Philosophy</span>
            <p className="text-xs text-slate-300 italic font-serif leading-relaxed">
              “True beauty is born from sincere love. Success is a journey that starts with you. Growth happens through challenges, and requires uncomfortable change.”
            </p>
          </div>

          <div className="p-5 rounded-3xl bg-slate-900/60 border border-slate-800 space-y-2">
            <span className="text-[10px] font-mono uppercase text-amber-400 font-bold">The Covenant</span>
            <p className="text-xs text-slate-300 italic font-serif leading-relaxed">
              “We started with 'Me'. Now it's 'U'. Together, we become 'Us'. Bringing Bangladesh alive — one opportunity at a time.”
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
